import React, { useCallback, useEffect, useRef, useState } from "react";
import PortalHeader from "@/components/PortalHeader";
import StatementsCard from "@/components/StatementsCard";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PhoneCall, PhoneOff, Mic, MicOff, Loader2, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { acquireSignaling, releaseSignaling } from "@/lib/signaling";
import { startVoiceCall } from "@/lib/call";

const STATUS_COLOR = {
  ringing: "bg-amber-100 text-amber-700 border-amber-200",
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ended: "bg-slate-100 text-slate-600 border-slate-200",
  missed: "bg-rose-100 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function CustomerPortal() {
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState("");
  const [activeCall, setActiveCall] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [calls, setCalls] = useState([]);
  const [billing, setBilling] = useState({ total_minutes: 0, total_calls: 0, total_charge: 0 });
  const [rates, setRates] = useState({ customer_rate_per_min: 0 });
  const [muted, setMuted] = useState(false);
  const [callStatus, setCallStatus] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const audioRef = useRef(null);
  const rtcRef = useRef(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const signalingRef = useRef(null);

  useEffect(() => {
    signalingRef.current = acquireSignaling();
    return () => releaseSignaling();
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [l, c, b, r] = await Promise.all([
        api.get("/languages"), api.get("/calls/mine/list"),
        api.get("/billing/mine"), api.get("/rates"),
      ]);
      setLanguages(l.data);
      setCalls(c.data);
      setBilling(b.data);
      setRates(r.data);
    } catch (e) {
      console.warn("[portal] load failed", e);
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const startRtc = useCallback((call) => {
    setCallStatus("connecting");
    rtcRef.current = startVoiceCall({
      signaling: signalingRef.current,
      peerId: call.interpreter_id,
      isCaller: true,
      onRemoteStream: (stream) => {
        if (audioRef.current) { audioRef.current.srcObject = stream; audioRef.current.play().catch(() => {}); }
      },
      onStatus: (s) => setCallStatus(s),
    });
  }, []);

  const cleanupCall = useCallback((notify = true) => {
    try { rtcRef.current?.close(); } catch (e) { console.warn("[portal] rtc close failed", e); }
    rtcRef.current = null;
    setCallStatus("");
    setMuted(false);
    setActiveCall(null);
    if (notify) toast("Call ended");
  }, []);

  useEffect(() => {
    if (!activeCall?.id) return;
    const callId = activeCall.id;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/calls/${callId}`);
        setActiveCall(data);
        if (data.status === "active" && !rtcRef.current) startRtc(data);
        if (["ended", "missed", "cancelled"].includes(data.status)) {
          cleanupCall(false);
          toast(data.status === "missed" ? "No interpreters available for this call." : `Call ${data.status}.`);
          loadAll();
        }
      } catch (e) {
        console.debug("[portal] call poll failed", e);
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [activeCall?.id, startRtc, cleanupCall, loadAll]);

  useEffect(() => {
    if (activeCall?.status === "active" && activeCall.answered_at) {
      const start = new Date(activeCall.answered_at).getTime();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
      return () => clearInterval(timerRef.current);
    }
    setElapsed(0);
  }, [activeCall?.status, activeCall?.answered_at]);

  const requestCall = async () => {
    if (!selectedLang) { toast.error("Choose a language"); return; }
    setRequesting(true);
    try {
      const { data } = await api.post("/calls/request", { language: selectedLang });
      setActiveCall(data);
      toast.success(`Calling ${data.interpreter_name}…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to place call");
    } finally {
      setRequesting(false);
    }
  };

  const endCall = async () => {
    if (!activeCall) return;
    try {
      await api.post(`/calls/${activeCall.id}/${activeCall.status === "ringing" ? "cancel" : "end"}`);
    } catch (e) { console.warn("[portal] end call failed", e); }
    cleanupCall();
    loadAll();
  };

  const toggleMute = () => {
    const next = !muted;
    rtcRef.current?.setMuted(next);
    setMuted(next);
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const showCallModal = !!activeCall && ["ringing", "active"].includes(activeCall.status);

  return (
    <div className="min-h-screen">
      <PortalHeader title="Client Portal" />
      <audio ref={audioRef} autoPlay />

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <section className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader>
              <CardTitle className="font-display text-xl">Call an interpreter</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
                <div>
                  <label className="text-sm font-medium text-slate-700">Language</label>
                  <Select value={selectedLang} onValueChange={setSelectedLang}>
                    <SelectTrigger className="mt-2 h-12" data-testid="language-select">
                      <SelectValue placeholder="Select a language" />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((l) => (
                        <SelectItem key={l.name} value={l.name} data-testid={`language-option-${l.name}`}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="lg" disabled={requesting || !!activeCall} onClick={requestCall}
                  data-testid="request-interpreter-btn"
                  className="h-12 px-8 rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90 font-semibold">
                  {requesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-2" />}
                  Call an Interpreter
                </Button>
              </div>
              <div className="text-sm text-slate-500 flex items-center gap-2">
                <Clock3 className="h-4 w-4" />
                Rate: <span className="font-medium text-slate-800">${rates.customer_rate_per_min?.toFixed(2)}/min</span>, billed to the second.
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="font-display text-xl">Your usage</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Kpi label="Total charge" value={`$${billing.total_charge.toFixed(2)}`} />
              <Kpi label="Total minutes" value={billing.total_minutes.toFixed(2)} />
              <Kpi label="Calls made" value={billing.total_calls} />
            </CardContent>
          </Card>
        </section>

        <section>
          <StatementsCard testidPrefix="customer-statements" />
        </section>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-display text-xl">Call history</CardTitle></CardHeader>
          <CardContent>
            <Table data-testid="customer-calls-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Interpreter</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Charge</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No calls yet</TableCell></TableRow>
                )}
                {calls.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{new Date(c.started_at).toLocaleString()}</TableCell>
                    <TableCell>{c.language}</TableCell>
                    <TableCell>{c.interpreter_name || "—"}</TableCell>
                    <TableCell>{fmt(c.duration_seconds || 0)}</TableCell>
                    <TableCell>${(c.customer_charge || 0).toFixed(2)}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_COLOR[c.status]}>{c.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {showCallModal && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 flex items-center justify-center p-6" data-testid="call-modal">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md p-8 text-center">
            <div className="relative mx-auto h-24 w-24 rounded-full bg-teal-50 flex items-center justify-center">
              {activeCall.status === "ringing" && <span className="itp-ping absolute inset-0 rounded-full" />}
              <PhoneCall className="h-8 w-8 text-teal-600" />
            </div>
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-slate-500">
                {activeCall.status === "ringing" ? "Ringing" : "In call"}
              </div>
              <div className="font-display text-2xl font-medium text-[#0a2540] mt-1">
                {activeCall.interpreter_name || "Finding an interpreter"}
              </div>
              <div className="text-slate-500 mt-1">{activeCall.language}</div>
              {activeCall.status === "active" && (
                <div className="mt-4 text-3xl font-display font-light tabular-nums" data-testid="call-timer">{fmt(elapsed)}</div>
              )}
              {callStatus && <div className="text-xs text-slate-400 mt-2">Connection: {callStatus}</div>}
            </div>
            <div className="mt-8 flex items-center justify-center gap-4">
              {activeCall.status === "active" && (
                <Button variant="outline" onClick={toggleMute} data-testid="mute-btn" className="h-12 w-12 rounded-full p-0">
                  {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>
              )}
              <Button onClick={endCall} data-testid="end-call-btn"
                className="h-12 px-6 rounded-full bg-rose-600 hover:bg-rose-700 text-white">
                <PhoneOff className="h-5 w-5 mr-2" />
                {activeCall.status === "ringing" ? "Cancel" : "End call"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-3 last:pb-0">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="font-display text-xl font-medium text-[#0a2540]">{value}</div>
    </div>
  );
}
